import type { StateCreator } from "zustand";
import type { MissionsCampaign } from "@/campaigns/modules/missions/types";
import type { MissionProgressSchema } from "@/types";
import {
  campaignKey,
  matchesKey,
  type CampaignKey,
} from "@/campaigns/campaign-key";
import type { CampaignStreamState } from "./campaigns.store";
import type { AppStore } from "./app.store";

export interface MissionsSlice {
  /** Assembled view objects (campaigns ⋈ mission_progress), rebuilt on stream change. */
  missionsCampaigns: MissionsCampaign[];
  setMissionsCampaigns: (missionsCampaigns: MissionsCampaign[]) => void;
  clearMissionsCampaigns: () => void;
  /** Per campaign, the id of the task on show in an any-order step — the launcher's rotation's
   *  turn (Sebastian, 2026-10-22: "a turn is a turn"): written by the rotation only, so the
   *  launcher's pixels and its spoken name read one memory. */
  missionFocusById: Record<CampaignKey, string>;
  /** Puts `taskId` on show for `key` (a new turn of the rotation). */
  setMissionFocus: (key: CampaignKey, taskId: string) => void;

  /** Raw `mission_progress` stream, grouped per campaign (the volatile tail). */
  missionProgressById: Record<CampaignKey, MissionProgressSchema[]>;
  missionProgressStream: CampaignStreamState;
  setMissionProgress: (progress: MissionProgressSchema[]) => void;
  setMissionProgressSubscribed: (subscribed: boolean) => void;
  setMissionProgressLoading: (loading: boolean) => void;
  clearMissionProgress: () => void;
}

const initialStream: CampaignStreamState = {
  subscribed: false,
  loading: false,
};

export const createMissionsSlice: StateCreator<MissionsSlice> = (set) => ({
  missionsCampaigns: [],
  setMissionsCampaigns: (missionsCampaigns) => set({ missionsCampaigns }),
  clearMissionsCampaigns: () =>
    set({ missionsCampaigns: [], missionFocusById: {} }),
  missionFocusById: {},
  setMissionFocus: (key, taskId) =>
    set((state) =>
      state.missionFocusById[key] === taskId
        ? state
        : { missionFocusById: { ...state.missionFocusById, [key]: taskId } }
    ),

  missionProgressById: {},
  missionProgressStream: initialStream,
  setMissionProgress: (progress) =>
    set(() => {
      // One stream element per mission; group by the owning campaign's key
      // (Mission has no custom_id — the campaign identifiers ride alongside).
      const byId: Record<CampaignKey, MissionProgressSchema[]> = {};
      for (const p of progress) {
        const key = campaignKey({
          id: p.campaign_id,
          custom_id: p.campaign_custom_id ?? undefined,
        });
        (byId[key] ??= []).push(p);
      }
      return { missionProgressById: byId };
    }),
  setMissionProgressSubscribed: (subscribed) =>
    set((state) => ({
      missionProgressStream: {
        ...state.missionProgressStream,
        subscribed,
        loading: false,
      },
    })),
  setMissionProgressLoading: (loading) =>
    set((state) => ({
      missionProgressStream: { ...state.missionProgressStream, loading },
    })),
  clearMissionProgress: () =>
    set({ missionProgressById: {}, missionProgressStream: initialStream }),
});

/** Resolve a missions campaign by its custom_id or numeric id (as string). */
export function selectMissionsCampaignById(
  state: AppStore,
  id: string
): MissionsCampaign | undefined {
  return state.missionsCampaigns.find((mc) => matchesKey(mc.campaign, id));
}
